import { create } from "zustand";

/**
 * Front-end side of the scope-aware baseline gate (PR #582).
 *
 * When a change touches baselined *scope*, the backend returns HTTP 423 with
 * a structured payload (see app/services/baseline_service._scope_block_payload).
 * The HTTP client surfaces that payload here; <ScopeChangeModal> renders it and
 * offers to open a change request pre-filled with the attempted change — so the
 * user is given the change-control path from the entity itself.
 */

export interface ScopeChangeRequestPrefill {
  target_entity_type: string;
  target_entity_id: string;
  baseline_id: string;
  change_type: string;
  scope_creep_flag: boolean;
  title: string;
  description: string;
}

export interface ScopeBlockPayload {
  locked: boolean;
  scope_change: boolean;
  reason: string;
  entity_type: string;
  entity_id: string;
  baseline_id: string;
  baseline_name: string;
  baseline_code: string;
  scope_fields: string[];
  attempted_change: Record<string, unknown>;
  message: string;
  change_request_prefill: ScopeChangeRequestPrefill;
}

/**
 * Narrow an arbitrary error-detail body to a ScopeBlockPayload. Validates both
 * the envelope and the change_request_prefill contents the modal reads, so a
 * malformed-but-shaped body cannot drive the form with undefined values.
 */
export function isScopeBlockPayload(d: unknown): d is ScopeBlockPayload {
  if (!d || typeof d !== "object") return false;
  const p = d as Partial<ScopeBlockPayload>;
  if (
    p.scope_change !== true ||
    typeof p.message !== "string" ||
    !Array.isArray(p.scope_fields) ||
    !p.change_request_prefill ||
    typeof p.change_request_prefill !== "object"
  ) {
    return false;
  }
  const pf = p.change_request_prefill as Partial<ScopeChangeRequestPrefill>;
  return (
    typeof pf.title === "string" &&
    typeof pf.description === "string" &&
    typeof pf.baseline_id === "string" &&
    typeof pf.target_entity_type === "string" &&
    typeof pf.target_entity_id === "string"
  );
}

interface ScopeBlockState {
  /** The active scope-block, or null when nothing is blocked. */
  block: ScopeBlockPayload | null;
  /** Bumped on every show() — keys the modal form so it resets per block. */
  seq: number;
  /** Surface a scope-block — opens <ScopeChangeModal>. */
  show: (block: ScopeBlockPayload) => void;
  /** Dismiss the modal. */
  dismiss: () => void;
}

export const useScopeBlockStore = create<ScopeBlockState>((set) => ({
  block: null,
  seq: 0,
  show: (block) => set((s) => ({ block, seq: s.seq + 1 })),
  dismiss: () => set({ block: null }),
}));
